/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law.
 */

import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'utils/toastUtils'

export type UiInlineSelectOption = {
    value: string | number
    label: React.ReactNode
}

interface UiInlineSelectProps {
    value: string | number
    options: UiInlineSelectOption[]
    onSave: (value: string | number) => Promise<unknown> | unknown
    ariaLabel: string
    className?: string
}

const UiInlineSelect: React.FC<UiInlineSelectProps> = ({
    value,
    options,
    onSave,
    ariaLabel,
    className,
}) => {
    const [currentValue, setCurrentValue] = useState(value)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => setCurrentValue(value), [value])

    const handleChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextValue = event.target.value
        const previousValue = currentValue
        setCurrentValue(nextValue)
        setIsSaving(true)
        try {
            await onSave(nextValue)
        } catch {
            setCurrentValue(previousValue)
            toast.error('Update failed')
        } finally {
            setIsSaving(false)
        }
    }

    const stopRowInteraction = (event: React.SyntheticEvent) => {
        event.stopPropagation()
    }

    return (
        <select
            aria-label={ariaLabel}
            value={currentValue}
            disabled={isSaving}
            data-row-selection-toggle="true"
            onMouseDown={stopRowInteraction}
            onClick={stopRowInteraction}
            onChange={handleChange}
            className={clsx(
                'min-w-28 max-w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm',
                'hover:border-gray-300 focus:border-cta-light focus:outline-none disabled:opacity-60',
                'dark:text-gray-100 dark:hover:border-gray-600',
                className
            )}
        >
            {options.map((option) => (
                <option key={String(option.value)} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    )
}

export default UiInlineSelect
